// Standard templates (seeded, read-only, can be switched off) and the
// superadmin's own presets (full CRUD). "Use" jumps to Compose prefilled.
import React, { useState } from "react";
import { Plus, Copy, Pencil, Trash2, Send, Lock, LayoutTemplate, BookmarkPlus, Users } from "lucide-react";
import { useBroadcastPage } from "../context";
import { broadcastApi } from "../api";
import { Btn, Modal, SectionCard, EmptyState } from "./ui";
import TemplateEditorModal from "./TemplateEditorModal";
import { severityColors, SEVERITY_META } from "../../../components/Broadcasts/broadcastTheme";

// One template as a table row (desktop) or a full-width card (phones) —
// same look as the Departments table.
const useTemplateParts = (tpl) => {
  const { s, t, theme, language } = useBroadcastPage();
  const T = t.templates;
  const color = severityColors(tpl.severity, theme);
  const SeverityIcon = (SEVERITY_META[tpl.severity] || SEVERITY_META.INFO).icon;
  const showAr = language === "ar" && (tpl.titleAr || tpl.bodyAr);
  const dimmed = { opacity: tpl.isActive ? 1 : 0.55, transition: "opacity 0.15s" };
  const chips = [
    t.categories[tpl.category],
    t.displayModes[tpl.displayMode]?.label,
    tpl.requireAck ? T.needsAck : null,
    tpl.defaultDurationHours ? T.hoursShort(tpl.defaultDurationHours) : null,
  ].filter(Boolean);

  const identity = (
    <div style={{ display: "flex", gap: 14, alignItems: "flex-start", ...dimmed }}>
      <span style={s.templateIcon(color)}>
        <SeverityIcon size={20} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ ...s.cellTitle, fontSize: 15.5 }}>{tpl.name}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {tpl.isSystem && (
            <span style={s.badge("#64748b")}>
              <Lock size={11} /> {T.standardBadge}
            </span>
          )}
          <span style={s.badge(color.accent)}>{t.severities[tpl.severity]}</span>
          {chips.map((c) => (
            <span key={c} style={s.chip}>
              {c}
            </span>
          ))}
        </div>
        {tpl.defaultAudience && (
          <div style={{ ...s.cellMeta, display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
            <Users size={13} /> {T.defaultAudience(tpl.defaultAudience.name)}
          </div>
        )}
      </div>
    </div>
  );

  const message = (
    <div style={{ fontSize: 14, lineHeight: 1.65, ...dimmed }} dir={showAr ? "rtl" : "ltr"}>
      <div style={{ fontWeight: 700, color: s.colors.text }}>{showAr ? tpl.titleAr || tpl.titleEn : tpl.titleEn}</div>
      <div style={{ color: s.colors.muted, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
        {showAr ? tpl.bodyAr || tpl.bodyEn : tpl.bodyEn}
      </div>
    </div>
  );

  return { s, identity, message };
};

const ActiveSwitch = ({ tpl, onToggle }) => {
  const { s, t } = useBroadcastPage();
  const T = t.templates;
  return (
    <label
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 600,
        color: tpl.isActive ? "#10b981" : s.colors.muted,
        whiteSpace: "nowrap",
      }}
    >
      <input
        type="checkbox"
        checked={tpl.isActive}
        onChange={(e) => onToggle(tpl, e.target.checked)}
        style={{ position: "absolute", opacity: 0, width: 1, height: 1 }}
      />
      <span aria-hidden="true" style={s.miniSwitch(tpl.isActive)}>
        <span style={s.miniSwitchKnob(tpl.isActive)} />
      </span>
      {tpl.isActive ? T.active : T.off}
    </label>
  );
};

const TemplateActions = ({ tpl, onUse, onEdit, onDuplicate, onDelete }) => {
  const { s, t } = useBroadcastPage();
  const T = t.templates;
  return (
    <div style={{ ...s.actionsCell, flexWrap: "nowrap" }}>
      <button
        type="button"
        className="bc-action-btn"
        style={{ ...s.actionBtn("primary"), opacity: tpl.isActive ? 1 : 0.5, cursor: tpl.isActive ? "pointer" : "not-allowed" }}
        disabled={!tpl.isActive}
        onClick={() => onUse(tpl)}
      >
        <Send size={14} /> {T.use}
      </button>
      <button type="button" className="bc-action-btn" style={s.actionBtn()} onClick={() => onDuplicate(tpl)} title={T.duplicate}>
        <Copy size={14} /> {T.duplicateShort}
      </button>
      {!tpl.isSystem && (
        <>
          <button type="button" className="bc-action-btn" style={s.actionBtn()} onClick={() => onEdit(tpl)}>
            <Pencil size={14} /> {t.edit}
          </button>
          <button type="button" className="bc-action-btn" style={s.actionBtn("danger")} onClick={() => onDelete(tpl)}>
            <Trash2 size={14} /> {t.delete}
          </button>
        </>
      )}
    </div>
  );
};

const TemplateRow = ({ tpl, ...handlers }) => {
  const { s, identity, message } = useTemplateParts(tpl);
  return (
    <tr className="bc-row">
      <td style={{ ...s.td, width: "34%" }}>{identity}</td>
      <td style={s.td}>{message}</td>
      <td style={{ ...s.td, width: 1, verticalAlign: "middle" }}>
        <ActiveSwitch tpl={tpl} onToggle={handlers.onToggle} />
      </td>
      <td style={{ ...s.td, width: 1, verticalAlign: "middle" }}>
        <TemplateActions tpl={tpl} {...handlers} />
      </td>
    </tr>
  );
};

const TemplateMobileCard = ({ tpl, ...handlers }) => {
  const { s, identity, message } = useTemplateParts(tpl);
  return (
    <div style={s.templateCard}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>{identity}</div>
        <ActiveSwitch tpl={tpl} onToggle={handlers.onToggle} />
      </div>
      <div style={s.templatePreview}>{message}</div>
      <div style={{ overflowX: "auto", paddingTop: 12, borderTop: `1px solid ${s.colors.rowBorder}` }}>
        <TemplateActions tpl={tpl} {...handlers} />
      </div>
    </div>
  );
};

const TemplateList = ({ items, handlers }) => {
  const { s, t, screenSize } = useBroadcastPage();
  const T = t.templates;
  if (screenSize.isMobile) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {items.map((tpl) => (
          <TemplateMobileCard key={tpl.id} tpl={tpl} {...handlers} />
        ))}
      </div>
    );
  }
  return (
    <div style={s.tableWrap}>
      <table style={s.table(980)}>
        <thead>
          <tr>
            <th style={s.th}>{T.colTemplate}</th>
            <th style={s.th}>{T.colMessage}</th>
            <th style={s.th}>{T.colStatus}</th>
            <th style={s.th}>{T.colActions}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((tpl) => (
            <TemplateRow key={tpl.id} tpl={tpl} {...handlers} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TemplatesTab = ({ onUse }) => {
  const { s, t, templates, refreshTemplates, notify } = useBroadcastPage();
  const T = t.templates;
  const [editing, setEditing] = useState(null); // template | {} (new) | null
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const system = templates.filter((x) => x.isSystem);
  const presets = templates.filter((x) => !x.isSystem);

  const run = async (fn, okMsg) => {
    try {
      await fn();
      await refreshTemplates();
      if (okMsg) notify("success", okMsg);
      return true;
    } catch (e) {
      notify("error", e.message);
      return false;
    }
  };

  const handlers = {
    onUse,
    onEdit: (tpl) => setEditing(tpl),
    onDuplicate: (tpl) => run(() => broadcastApi.duplicateTemplate(tpl.id), T.duplicated(tpl.name)),
    onDelete: (tpl) => setDeleting(tpl),
    onToggle: (tpl, isActive) => run(() => broadcastApi.updateTemplate(tpl.id, { isActive })),
  };

  return (
    <div>
      <SectionCard
        icon={BookmarkPlus}
        title={
          <>
            {T.yourPresets} <span style={s.countPill}>{presets.length}</span>
          </>
        }
        subtitle={T.yourPresetsSub}
        actions={
          <Btn onClick={() => setEditing({})}>
            <Plus size={16} /> {T.newPreset}
          </Btn>
        }
      >
        {presets.length === 0 ? (
          <EmptyState icon={BookmarkPlus}>{T.noPresets}</EmptyState>
        ) : (
          <TemplateList items={presets} handlers={handlers} />
        )}
      </SectionCard>

      <SectionCard
        icon={LayoutTemplate}
        title={
          <>
            {T.standard} <span style={s.countPill}>{system.length}</span>
          </>
        }
        subtitle={T.standardSub}
      >
        <TemplateList items={system} handlers={handlers} />
      </SectionCard>

      {editing && (
        <TemplateEditorModal
          template={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refreshTemplates();
          }}
        />
      )}

      {deleting && (
        <Modal
          title={T.deleteTitle}
          icon={Trash2}
          onClose={() => setDeleting(null)}
          busy={busy}
          footer={
            <>
              <Btn kind="secondary" disabled={busy} onClick={() => setDeleting(null)}>
                {t.cancel}
              </Btn>
              <Btn
                kind="danger"
                disabled={busy}
                onClick={async () => {
                  const tpl = deleting;
                  setBusy(true);
                  const ok = await run(() => broadcastApi.deleteTemplate(tpl.id), T.deleted(tpl.name));
                  setBusy(false);
                  if (ok) setDeleting(null);
                }}
              >
                <Trash2 size={16} /> {t.delete}
              </Btn>
            </>
          }
        >
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7 }}>{T.deleteBody(deleting.name)}</p>
        </Modal>
      )}
    </div>
  );
};

export default TemplatesTab;
